import * as Sentry from '@sentry/react'
import axios, { AxiosError, AxiosRequestConfig } from 'axios'
import { API_URL } from '../common/constants'
import { authenticateFailure } from '../store/auth/actions'
import { refreshAccessToken } from './auth/refresh'
import { clearTokens, getAccessToken } from './auth/tokenStorage'

// caps refresh-and-retry at one attempt per request — without it, a request
// that still 401s after a refresh would trigger another refresh-and-retry, forever
interface RetryableRequestConfig extends AxiosRequestConfig {
    retried?: boolean
}

const NO_AUTH_HEADER_URLS = ['login_check', 'token/refresh', 'token/invalidate', 'v1/core/labels']
const AUTH_ENTRYPOINT_URLS = ['login_check', 'v1/core/backoffice/impersonation/exchange']

export const apiClient = axios.create({
    baseURL: API_URL,
    timeout: 30000,
})

// dynamic import avoids a circular dependency: client.ts is reached via
// store/middleware/api.js → store/auth → store/auth/sagas.js → client.ts,
// all while src/store/index.js itself is still mid-evaluation
const logoutWithSessionExpired = async (): Promise<void> => {
    clearTokens()
    const { store } = await import('../store')
    store.dispatch(authenticateFailure({ status: 401, message: 'session_expired' }))
}

// Sentry report only for server errors
const rejectAndReport = (error: AxiosError): Promise<never> => {
    const status = error.response?.status

    if (!status || status >= 500) {
        Sentry.captureException(error)
    }

    return Promise.reject(error)
}

apiClient.interceptors.request.use((config: RetryableRequestConfig) => {
    const skipAuthHeader = NO_AUTH_HEADER_URLS.some((url) => config.url?.startsWith(url))
    const accessToken = getAccessToken()

    config.headers = {
        ...config.headers,
        locale: config.headers?.locale ?? localStorage.getItem('i18nextLng') ?? '',
    }

    if (!skipAuthHeader && accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`
    }

    return config
})

apiClient.interceptors.response.use(
    (response) => response,
    async (error: AxiosError<{ message?: string }>) => {
        const config = error.config as RetryableRequestConfig
        const { response } = error

        // none of the refresh/logout logic below applies
        // just reject and caller (query/mutation) will handle error itself
        if (response?.status !== 401) {
            return rejectAndReport(error)
        }

        // there's no session to refresh here, so a 401 should pass the real error straight back to the caller
        if (AUTH_ENTRYPOINT_URLS.some((url) => config.url?.startsWith(url))) {
            return rejectAndReport(error)
        }

        const isRefreshRequest = config.url?.startsWith('token/refresh')
        const isInvalidJwt = response.data?.message === 'Invalid JWT Token'

        // unrecoverable via refresh: the refresh call itself failed, this request
        // already went through one refresh-and-retry, or the token is flat-out invalid
        if (isRefreshRequest || config.retried || isInvalidJwt) {
            console.log('Unrecoverable error, logging out:', error)
            await logoutWithSessionExpired()
            return rejectAndReport(error)
        }

        // the normal case — access token expired, refresh once and replay this request
        try {
            const newToken = await refreshAccessToken()
            config.retried = true
            config.headers = { ...config.headers, Authorization: `Bearer ${newToken}` }

            return apiClient(config)

            // the refresh attempt itself failed, for any reason
        } catch (refreshError) {
            await logoutWithSessionExpired()
            return rejectAndReport(refreshError as AxiosError)
        }
    }
)
